import { MutableRefObject, useContext, useEffect, useRef, useState } from 'react';
import { TableState } from '@tanstack/react-table';
import { TableContext } from 'components/table/TableContext';
import { useTableId } from 'components/table/useTableId';
import { createWithEqualityFn as create } from 'zustand/traditional';

interface TableStateStore {
    tables: { [id: string]: TableState | undefined };
    setTableStateById: (id: string, tableState?: TableState) => void;
}

const useTableStateStore = create<TableStateStore>(set => ({
    tables: {},
    setTableStateById: (id, tableState) =>
        set(state => ({
            ...state,
            tables: {
                ...state.tables,
                [id]: tableState,
            },
        })),
}));

const usePersistentTableState = (ref: MutableRefObject<HTMLTableElement | null>) => {
    const { isPersisted = true, table } = useContext(TableContext);
    const [didRestore, setDidRestore] = useState(false);
    const { setTableStateById, tables } = useTableStateStore();
    const tableId = useTableId(ref);
    const previousTableState = tableId ? tables[tableId] : undefined;
    const isEnabledRef = useRef(isPersisted);
    const idRef = useRef(tableId);
    const tableRef = useRef(table);
    const setTableStateByIdRef = useRef(setTableStateById);

    useEffect(() => {
        idRef.current = tableId;
    }, [tableId]);

    useEffect(() => {
        tableRef.current = table;
    }, [table]);

    useEffect(() => {
        setTableStateByIdRef.current = setTableStateById;
    }, [setTableStateById]);

    // key here is to only propagate to the store on unmount, hence capturing refs above
    useEffect(() => {
        const isEnabled = isEnabledRef.current;
        return () => {
            if (isEnabled && idRef.current) {
                setTableStateByIdRef.current(idRef.current, tableRef.current?.getState());
            }
        };
    }, []);

    useEffect(() => {
        if (isPersisted && tableId && !didRestore) {
            // we predominantly use manual table state, hence we need to map and propagate manually instead of calling table.setState()
            if (previousTableState) {
                if (table.options.manualPagination) {
                    table.setPagination(previousTableState.pagination);
                }
                if (table.options.manualSorting) {
                    table.setSorting(previousTableState.sorting);
                }
                if (table.options.manualExpanding) {
                    table.setExpanded(previousTableState.expanded);
                }
                table.setColumnVisibility(previousTableState.columnVisibility);
                table.setColumnPinning(previousTableState.columnPinning);
                table.setColumnOrder(previousTableState.columnOrder);
                table.setRowSelection(previousTableState.rowSelection);
            }
            setDidRestore(true);
        }
    }, [didRestore, isPersisted, previousTableState, table, tableId]);

    return { isHydrated: didRestore };
};

export { usePersistentTableState, useTableStateStore };
